import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Profile } from '../../@types/Profile.type';
import { Catalogs } from '../../@types/Catalogs.type';
import { Receiver } from '../../@types/Receiver.type';

import { SubscriptionType } from '../../@types/Subscription';
import { UserSavedProduct } from '../../@types/Invoicing.type';
import { isAnnualDeclarationSubscription } from '../../shared/annual/isAnnualDeclarationSubscription';
import { FftItem } from '../../@types/FFT.type';
import { UserTag } from '../../@types/Tagging.type';
import { NSSValidationResponse } from '../../@types/IMSS.type';
import { CrawlEventResponse } from '../../@types/Crawl.type';
import { Notification } from '@/features/notifications/types';


export enum PlatformOS {
	WEB = 'web',
	IOS = 'ios',
	ANDROID = 'android',
}

export interface ProfileState {
	user?: Profile;
	catalogs?: Catalogs;
	receivers: Receiver[];
	userProducts?: UserSavedProduct[];
	subscriptions: SubscriptionType[];
	activeSuscriptions: SubscriptionType[];
	activeAnnualSubscription: SubscriptionType | undefined;
	isInApp?: boolean;
	serviceStatus?: FftItem;
	isFromIOS?: boolean;
	isHotSale?: boolean;
	priceGroupExperiment?: string;
	errors: {
		[key: string | number]: string;
	};
	tags: UserTag[];
	nss: NSSValidationResponse | null;
	userExtractionEvents: CrawlEventResponse[];
	notifications: Notification[] | null;
}

const initialState: ProfileState = {
	receivers: [],
	subscriptions: [],
	activeSuscriptions: [],
	activeAnnualSubscription: undefined,
	isInApp: false,
	serviceStatus: undefined,
	isFromIOS: false,
	isHotSale: false,
	errors: {},
	priceGroupExperiment: '',
	tags: [],
	nss: null,
	userExtractionEvents: [],
	notifications: [],
};

export const profileSlice = createSlice({
	name: 'profile',
	initialState,
	reducers: {
		setProfile: (state, action: PayloadAction<Profile>) => {
			state.user = action.payload;
		},
		clearProfile: (state) => {
			state.user = undefined;
		},
		setCatalogs: (state, action: PayloadAction<Catalogs>) => {
			state.catalogs = action.payload;
		},
		setReceivers: (state, action: PayloadAction<Receiver[]>) => {
			state.receivers = action.payload;
		},
		setUserProducts: (state, action: PayloadAction<any>) => {
			state.userProducts = action.payload;
		},
		setUserSubscriptions: (
			state,
			action: PayloadAction<SubscriptionType[]>,
		) => {
			state.subscriptions = action.payload;
			state.activeSuscriptions = action.payload.filter(
				(subscription) => subscription.active,
			);
			state.activeAnnualSubscription = action.payload.find(
				(subscription) =>
					subscription?.active &&
					isAnnualDeclarationSubscription(subscription),
			);
		},
		setServiceStatusElement: (state, action: PayloadAction<FftItem>) => {
			state.serviceStatus = action.payload;
		},
		setIsHotSale: (state, action) => {
			state.isHotSale = action.payload;
		},
		setAllErrors: (state, action) => {
			state.errors = action.payload;
		},
		setPriceGroupExperiment: (state, action) => {
			state.priceGroupExperiment = action.payload;
		},
		setTags: (state, action: PayloadAction<UserTag[]>) => {
			state.tags = action.payload;
		},
		setNss: (state, action: PayloadAction<any>) => {
			state.nss = action.payload;
		},
		setUserExtractionEvents: (state, action: PayloadAction<CrawlEventResponse[]>) => {
			state.userExtractionEvents = action.payload;
		},
		setNotifications: (state, action: PayloadAction<Notification[]>) => {
			state.notifications = action.payload;
		},
	},
});

export const {
	setProfile,
	clearProfile,
	setCatalogs,
	setReceivers,
	setUserProducts,
	setUserSubscriptions,
	setServiceStatusElement,
	setIsHotSale,
	setAllErrors,
	setPriceGroupExperiment,
	setTags,
	setNss,
	setUserExtractionEvents,
	setNotifications,
} = profileSlice.actions;

export default profileSlice.reducer;
